import './check-item-discussion.scss'
import React, {
  Fragment,
  useCallback,
  useState,
} from 'react'
import {
  useTranslation,
} from 'react-i18next'
import {
  Box,
  Button,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import {
  FileUpload,
  SupportAgent,
} from '@vedmarket/ui-kit'
import moment from 'moment'
import {
  APPLICATION_DOCUMENT_ACCEPTED_FILES,
  COLORS,
} from 'config/constants'
import {
  APPLICATION_CHECK_STATUS,
  APPLICATION_EVENT_TYPE,
} from '../../../../../config/constants'

import {
  blockClosingManagerNoteBody,
  buildBlockTimeline,
  checkItemStatusLabelFromPayload,
  clarificationsForCheckKind,
  eventsForCheckKind,
} from '../check-block-utils'
import CheckItemPreparedDocuments from './check-item-prepared-documents'
import ApplicationDocumentLink from './application-document-link'

function authorInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (!parts.length) {
    return '?'
  }
  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase()
  }
  return `${parts[0][0] ?? ''}${parts[parts.length - 1][0] ?? ''}`.toUpperCase()
}

const CheckItemDiscussion = ({
  app,
  item,
  managerView,
  showEvents = false,
  checkStatuses,
  showComposer,
  runAction,
  onPostComment,
}: ApplicationCheckItemDiscussionProps) => {
  const { t } = useTranslation()

  const [ body, setBody ] = useState('')
  const [ pendingFiles, setPendingFiles ] = useState<File[]>([])

  const clarifications = clarificationsForCheckKind({
    clarifications: app.clarifications,
    kind: item.kind,
    kindLabel: item.kind_label,
  })
  const events = showEvents ? eventsForCheckKind({ events: app.events, kind: item.kind }) : []
  const comments = item.comments ?? []
  const timeline = buildBlockTimeline({ clarifications, events, comments })
  const isBlockCompleted = item.status === APPLICATION_CHECK_STATUS.COMPLETED
  const closingManagerNote = blockClosingManagerNoteBody({ item, clarifications })
  const closingBody = closingManagerNote
  const closingDocuments = isBlockCompleted ? (item.prepared_documents ?? []) : []
  const hasClosingContent = Boolean(closingBody) || closingDocuments.length > 0
  const showClosingSection = isBlockCompleted && hasClosingContent
  const showTimeline = timeline.length > 0

  const canSend = Boolean(body.trim()) || pendingFiles.length > 0

  const appendFiles = useCallback((picked: File[]) => {
    if (picked.length) setPendingFiles((current) => [ ...current, ...picked ])
    return false
  }, [])

  const removePendingFile = (index: number) => {
    setPendingFiles((current) => current.filter((_, fileIndex) => fileIndex !== index))
  }

  const handleSend = () => {
    if (!canSend) return
    runAction(() => onPostComment({
      kind: item.kind,
      body: body.trim(),
      attachments: pendingFiles,
    }).then(() => {
      setBody('')
      setPendingFiles([])
    }), 'applications__message__commentSent')
  }

  const renderAttachments = (attachments: ApplicationMediaFile[] | undefined) => {
    if (!attachments?.length) return null
    return (
      <ul className='application-check-item-discussion__attachments'>
        {attachments.map((doc) => (
          <li key={doc.id}>
            <ApplicationDocumentLink document={doc} />
          </li>
        ))}
      </ul>
    )
  }

  const renderNoteCard = ({
    itemKey,
    authorName,
    roleLabel,
    createdAt,
    body,
    attachments,
    isClient,
    isManager = false,
    isClosing = false,
    footer,
  }: ApplicationCheckItemDiscussionNoteArgs) => {
    const isOwnMessage = managerView ? isManager : isClient

    return (
      <div
        key={itemKey}
        className={[
          'application-check-item-discussion__timeline-item',
          'application-check-item-discussion__timeline-item--note',
          isClient ? 'application-check-item-discussion__timeline-item--client' : '',
          !isOwnMessage ? 'application-check-item-discussion__timeline-item_other' : '',
          isClosing ? 'application-check-item-discussion__timeline-item--closing' : '',
        ].filter(Boolean).join(' ')}
      >
        <div className='application-check-item-discussion__timeline-aside'>
          <div
            className='application-check-item-discussion__avatar'
            role={isManager ? 'img' : undefined}
            aria-label={isManager ? t('applications__text__blockDiscussionManager') : undefined}
            aria-hidden={isManager ? undefined : true}
          >
            {isManager ? (
              <SupportAgent width={24} height={24} aria-hidden focusable='false' />
            ) : authorInitials(authorName)}
          </div>
        </div>
        <article
          className={[
            'application-check-item-discussion__note-card',
            isClosing ? 'application-check-item-discussion__note-card--closing' : '',
          ].filter(Boolean).join(' ')}
        >
          <header className='application-check-item-discussion__note-header'>
            <div className='application-check-item-discussion__note-meta'>
              <Typography color={managerView ? 'text.primary' : undefined} component='span' variant='body2' className='application-check-item-discussion__note-author'>
                {authorName}
              </Typography>
              {roleLabel && (
                <span className='application-check-item-discussion__role-badge' style={{
                  color: managerView ? COLORS.BLACK_DARK : undefined,
                }}>
                  {roleLabel}
                </span>
              )}
            </div>
            <time className='application-check-item-discussion__note-time' style={{
              color: managerView ? COLORS.BLACK_DARK : undefined,
            }} dateTime={createdAt}>
              {moment(createdAt).fromNow()}
            </time>
          </header>
          {body && (
            <Typography color={managerView ? 'text.primary' : undefined} variant='body2' component='div' className='application-check-item-discussion__note-body'>
              {body}
            </Typography>
          )}
          {renderAttachments(attachments)}
          {footer && (
            <div className='application-check-item-discussion__note-footer'>
              {footer}
            </div>
          )}
        </article>
      </div>
    )
  }

  const hasTimelineOrClosing = showTimeline || showClosingSection
  const hasVisibleContent = hasTimelineOrClosing || showComposer

  if (!hasVisibleContent) {
    return null
  }

  return (
    <div
      className={[
        'application-check-item-discussion',
        isBlockCompleted ? 'application-check-item-discussion--block-completed' : '',
      ].filter(Boolean).join(' ')}
    >
      {showTimeline && (
        <div className='application-check-item-discussion__timeline'>
          {timeline.map((entry) => {
            if (entry.kind === 'comment') {
              const comment = entry.comment
              let roleLabel: string | undefined
              if (!comment.posted_as_manager) {
                roleLabel = t('applications__text__blockDiscussionClient')
              } else if (comment.posted_as_manager) {
                roleLabel = t('applications__text__blockDiscussionManager')
              }
              const authorName = comment.author_name || roleLabel || ''
              return renderNoteCard({
                itemKey: `comment-${comment.id}`,
                authorName,
                roleLabel,
                createdAt: comment.created_at,
                body: comment.body,
                attachments: comment.attachments,
                isClient: !comment.posted_as_manager,
                isManager: comment.posted_as_manager,
              })
            }

            if (entry.kind === 'clarification') {
              const c = entry.clarification
              const managerLabel = t('applications__text__blockDiscussionManager')
              const clientLabel = t('applications__text__blockDiscussionClient')
              return (
                <Fragment key={`clar-${c.id}`}>
                  {renderNoteCard({
                    itemKey: `clar-${c.id}-q`,
                    authorName: managerLabel,
                    roleLabel: t('applications__text__managerRequest'),
                    createdAt: c.created_at,
                    body: c.question,
                    isClient: false,
                    isManager: true,
                  })}
                  {c.answer_text && renderNoteCard({
                    itemKey: `clar-${c.id}-a`,
                    authorName: clientLabel,
                    roleLabel: t('applications__text__yourAnswer'),
                    createdAt: c.answered_at || c.created_at,
                    body: c.answer_text,
                    attachments: c.attachments,
                    isClient: true,
                  })}
                </Fragment>
              )
            }

            if (entry.kind !== 'event') {
              return null
            }

            const event = entry.event
            const statusLabel = event.event_type === APPLICATION_EVENT_TYPE.CHECK_ITEM_UPDATED
              ? checkItemStatusLabelFromPayload({ payload: event.payload, checkStatuses })
              : null
            const clarTexts = new Set(clarifications.map((c) => c.question.trim()))
            const eventMessage = (event.message || '').trim()
            const showMessage = Boolean(eventMessage)
              && !clarTexts.has(eventMessage)
              && eventMessage !== closingManagerNote

            return (
              <div
                key={`event-${event.id}`}
                className='application-check-item-discussion__timeline-item application-check-item-discussion__timeline-item--system'
              >
                <div className='application-check-item-discussion__timeline-aside' aria-hidden>
                  <span className='application-check-item-discussion__timeline-dot' />
                </div>
                <div className='application-check-item-discussion__system-entry'>
                  <p className='application-check-item-discussion__system-line'>
                    {event.actor_name && (
                      <strong className='application-check-item-discussion__system-actor'>
                        {event.actor_name}
                      </strong>
                    )}
                    {event.actor_name ? ' ' : ''}
                    <span>{event.event_type_label}</span>
                    {statusLabel && (
                      <span className='application-check-item-discussion__system-detail' style={{
                        color: managerView ? COLORS.BLACK_DARK : undefined,
                      }}>
                        {' · '}
                        {statusLabel}
                      </span>
                    )}
                  </p>
                  <time className='application-check-item-discussion__system-time' style={{
                    color: managerView ? COLORS.BLACK_DARK : undefined,
                  }} dateTime={event.created_at}>
                    {moment(event.created_at).fromNow()}
                  </time>
                  {showMessage && (
                    <Typography color={managerView ? 'text.primary' : undefined} variant='body2' component='p' className='application-check-item-discussion__system-message'>
                      {event.message}
                    </Typography>
                  )}
                </div>
              </div>
            )
          })}

        </div>
      )}

      {showClosingSection && (
        <Stack spacing={3}>
          {closingBody && (
            <Stack spacing={1}>
              <Typography variant='caption' color={managerView ? 'text.primary' : 'text.secondary'}>
                {t('applications__text__blockManagerComment')}:
              </Typography>
              <Typography color={managerView ? 'text.primary' : undefined} variant='body2' sx={{
                whiteSpace: 'pre-wrap',
                overflowWrap: 'anywhere',
              }}>
                {closingBody}
              </Typography>
            </Stack>
          )}
          {closingDocuments.length > 0 && (
            <CheckItemPreparedDocuments managerView={managerView} documents={closingDocuments} readOnly layout='closingEmbedded' />
          )}
        </Stack>
      )}

      {showComposer && (
        <Stack spacing={3}>
          <TextField
            fullWidth
            size='small'
            multiline
            minRows={3}
            InputLabelProps={{ shrink: true }}
            label={<Typography component='span' variant='inherit' color={managerView ? 'text.primary' : undefined}>{t('applications__text__blockDiscussionCompose')}</Typography>}
            placeholder={t('applications__text__blockDiscussionComposePlaceholder')}
            value={body}
            onChange={(e) => setBody(e.target.value)}
          />
          {pendingFiles.map((file, index) => (
            <FileUpload
              key={`${file.name}-${file.lastModified}-${index}`}
              acceptedFilesConfig={APPLICATION_DOCUMENT_ACCEPTED_FILES}
              initialFileTitle={file.name}
              onRemove={() => removePendingFile(index)}
            />
          ))}
          <FileUpload multiple acceptedFilesConfig={APPLICATION_DOCUMENT_ACCEPTED_FILES} onHandleFiles={appendFiles} />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button variant='contained' disabled={!canSend} onClick={handleSend}>
              <Typography component='span' variant='inherit'>
                {t('applications__text__blockDiscussionSend')}
              </Typography>
            </Button>
          </Box>
        </Stack>
      )}
    </div>
  )
}

export default CheckItemDiscussion
